import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { Home, Upload, ClipboardList, MessagesSquare, BookOpen, History as HistoryIcon, LogOut, ChevronDown, Check, Building2, ShieldCheck, Calculator, MoreHorizontal, Banknote, Wallet, Bell, Menu, LineChart, Folder, Users, TrendingDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import DataSourceGate from "@/components/DataSourceGate";
import InstallPrompt from "@/components/InstallPrompt";
import OfflineBanner from "@/components/OfflineBanner";

// Nav items with `flag` — the corresponding portal_features key. Items without
// a flag (Home, Upload) are always visible.
const PRIMARY_NAV = [
    { to: "/portal", end: true, label: "Home", icon: Home, key: "home" },
    { to: "/portal/messages", label: "Messages", icon: MessagesSquare, key: "messages", flag: "messages" },
    { to: "/portal/cash-register", label: "Cash register", icon: Wallet, key: "cash-register", flag: "cash_register" },
    { to: "/portal/cash-out", label: "Where your money went", icon: Banknote, key: "cash-out", flag: "spend_insights" },
    { to: "/portal/tax-liability", label: "My Tax Liabilities", icon: Calculator, key: "tax-position", flag: "tax_position" },
    { to: "/portal/debtors", label: "Debtors", icon: Users, key: "debtors", flag: "debtors" },
    { to: "/portal/debt", label: "Eradicator", icon: TrendingDown, key: "debt", flag: "debt" },
    { to: "/portal/compliance", label: "Compliance", icon: ShieldCheck, key: "compliance", flag: "compliance" },
];
const MORE_NAV = [
    { to: "/portal/returns", label: "Tax returns", icon: Banknote, key: "returns", flag: "tax_returns" },
    { to: "/portal/requests", label: "Requests", icon: ClipboardList, key: "requests", flag: "requests" },
    { to: "/portal/history", label: "History", icon: HistoryIcon, key: "history", flag: "history" },
    { to: "/portal/help", label: "Help", icon: BookOpen, key: "help", flag: "help" },
];

// Bottom tab bar — five fixed slots matching the "Your business. Your data.
// Your answers." spec. Upload is the raised centre pill. Each non-required
// tab is gated on a portal_features flag OR an add-on flag ('addon' key):
// when off the tab is skipped entirely, so a "testing" preset ends up with
// just Home · Cash Register · Upload · Messages.
const BOTTOM_TABS = [
    { to: "/portal", end: true, label: "Home", icon: Home, key: "home" },
    { to: "/portal/cash-register", label: "Cash", icon: Wallet, key: "cash-register", addon: "cash_register" },
    { to: "/portal/tax-liability", label: "Insights", icon: LineChart, key: "insights", flag: "tax_position" },
    { to: "/portal/upload", label: "Upload", icon: Upload, key: "upload", raised: true, flag: "upload" },
    { to: "/portal/history", label: "Documents", icon: Folder, key: "documents", flag: "history" },
    { to: "/portal/messages", label: "Messages", icon: MessagesSquare, key: "messages", flag: "messages" },
];

// Given a resolved feature map, return only the nav items the client should see.
function filterNav(items, features) {
    if (!features) return items;
    return items.filter((it) => !it.flag || features[it.flag] !== false);
}

function EntitySwitcher() {
    const { user, clients, activeClient, setActiveClientId, combinedView, setCombinedView } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    if (!clients || clients.length <= 1) {
        return (
            <div className="text-xs font-medium truncate max-w-[160px]" data-testid="portal-client-name" title={activeClient?.name}>
                {activeClient?.name || "Your account"}
            </div>
        );
    }
    const triggerLabel = combinedView
        ? `All ${clients.length} entities`
        : (activeClient?.name || "Pick entity");
    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#0E3B2E]/20 bg-emerald-50 px-2.5 py-1.5 text-xs hover:bg-emerald-100 transition-colors"
                data-testid="portal-entity-switcher"
                title={`Switch between the ${clients.length} entities linked to ${user?.email || "your login"}`}
            >
                <Building2 className="h-3.5 w-3.5 text-[#0E3B2E]" />
                <span className="font-medium max-w-[120px] sm:max-w-[180px] truncate">{triggerLabel}</span>
                <span className="rounded-full bg-[#0E3B2E] text-white px-1.5 py-0.5 text-[10px] font-mono">{clients.length}</span>
                <ChevronDown className={`h-3 w-3 text-[#0E3B2E] transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    className="absolute right-0 mt-1.5 w-80 z-40 rounded-lg border border-[#E4E4E7] bg-white shadow-lg overflow-hidden"
                    data-testid="portal-entity-list"
                >
                    <div className="px-3 py-2.5 border-b border-[#E4E4E7] bg-emerald-50/60">
                        <div className="co-label text-[#0E3B2E]">Signed in as</div>
                        <div className="mt-0.5 text-sm font-mono truncate" data-testid="portal-signed-in-email" title={user?.email}>
                            {user?.email || "you"}
                        </div>
                        <div className="mt-1.5 text-[11px] text-[#5E636E] leading-relaxed">
                            This one login controls <strong>all {clients.length} entities</strong> below. Uploads, messages, and every action land under whichever entity is ticked here.
                        </div>
                    </div>
                    {/* Combined-view row — always sits above the single entities */}
                    <button
                        type="button"
                        onClick={() => { setCombinedView(true); setOpen(false); }}
                        className={`w-full flex items-start justify-between gap-3 px-3 py-2.5 text-left border-b border-[#E4E4E7] hover:bg-zinc-50 ${combinedView ? "bg-emerald-50/60" : ""}`}
                        data-testid="portal-entity-pick-combined"
                    >
                        <div className="min-w-0">
                            <div className="text-sm font-medium">All entities combined</div>
                            <div className="mt-0.5 text-[11px] text-[#5E636E]">See the whole picture — totals across every entity you own.</div>
                        </div>
                        {combinedView && <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />}
                    </button>
                    <ul className="max-h-80 overflow-y-auto">
                        {clients.map((c) => {
                            const active = !combinedView && activeClient?.id === c.id;
                            return (
                                <li key={c.id}>
                                    <button
                                        type="button"
                                        onClick={() => { setActiveClientId(c.id); setOpen(false); }}
                                        className={`w-full flex items-start justify-between gap-3 px-3 py-2.5 text-left hover:bg-zinc-50 ${active ? "bg-emerald-50/60" : ""}`}
                                        data-testid={`portal-entity-pick-${c.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{c.name}</div>
                                            {c.external_id && (
                                                <div className="mt-0.5 text-[11px] text-[#5E636E] font-mono">IRD {c.external_id}</div>
                                            )}
                                        </div>
                                        {active && <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default function PortalLayout() {
    const { user, tenant, logout, activeClient } = useAuth();
    const nav = useNavigate();
    const [moreOpen, setMoreOpen] = useState(false);
    const moreRef = useRef(null);
    const mobileMoreRef = useRef(null);
    // Per-client feature flags — re-fetched whenever the active client changes
    // so multi-entity users see the correct nav after switching.
    const [features, setFeatures] = useState(null);
    useEffect(() => {
        let alive = true;
        const previewId = (() => {
            try { return new URL(window.location.href).searchParams.get("preview_client_id"); }
            catch { return null; }
        })();
        api.get("/portal/features", { params: previewId ? { client_id: previewId } : {} })
            .then((r) => { if (alive) setFeatures(r.data?.resolved || null); })
            .catch(() => { if (alive) setFeatures(null); });
        return () => { alive = false; };
    }, [activeClient?.id]);
    const primaryNav = filterNav(PRIMARY_NAV, features);
    const moreNav = filterNav(MORE_NAV, features);

    useEffect(() => {
        function onDoc(e) {
            if (moreRef.current && moreRef.current.contains(e.target)) return;
            if (mobileMoreRef.current && mobileMoreRef.current.contains(e.target)) return;
            setMoreOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    async function handleLogout() {
        await logout();
        nav("/", { replace: true });
    }

    // Firm-side users (firm_admin / firm_staff) don't belong on /portal — the
    // portal is the client daily surface. On the native mobile build this
    // becomes the "please use the desktop admin" gate.
    // Exception: `?preview_client_id=<id>` lets an admin preview the portal
    // exactly as that client sees it. We show a persistent banner and honour
    // the query param via `activeClientId` (see AuthProvider).
    const previewClientId = (() => {
        try { return new URL(window.location.href).searchParams.get("preview_client_id"); }
        catch { return null; }
    })();
    const isPreview = !!(user && user.role && user.role !== "client" && previewClientId);
    const [previewClientName, setPreviewClientName] = useState(null);
    useEffect(() => {
        if (!isPreview) return;
        api.get(`/clients/${previewClientId}`)
            .then((r) => setPreviewClientName(r.data?.name || null))
            .catch(() => setPreviewClientName(null));
    }, [isPreview, previewClientId]);
    if (user && user.role && user.role !== "client" && !previewClientId) {
        return <FirmOnDesktopScreen user={user} onLogout={logout} />;
    }

    return (
        <DataSourceGate>
        <div className="min-h-screen bg-[#F5F4EF] flex flex-col pb-20 sm:pb-0 text-[#0F1115]">
            {/* Native/PWA connectivity banner — a no-op when online. */}
            <OfflineBanner />
            {isPreview && (
                <div className="sticky top-0 z-40 bg-amber-100 border-b border-amber-300 text-[13px] text-amber-900 px-4 py-2 flex items-center justify-between gap-3" data-testid="portal-preview-banner">
                    <div>
                        <b>Admin preview</b> · you are viewing this portal as <b>{previewClientName || activeClient?.name || "client"}</b>. Nothing you click here is logged as their activity.
                    </div>
                    <button
                        type="button"
                        onClick={() => { window.close(); nav("/app", { replace: true }); }}
                        className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-[12px] font-medium text-amber-900 hover:bg-amber-50"
                        data-testid="portal-preview-exit"
                    >
                        Exit preview
                    </button>
                </div>
            )}
            {/* Top bar — brand header w/ tagline + notification bell. The
                safe-area-inset-top padding keeps content clear of the iPhone
                notch when running as an installed PWA / native app. */}
            <header
                className="sticky top-0 z-30 border-b border-[#E4E4E7] bg-white/95 backdrop-blur"
                style={{ paddingTop: "env(safe-area-inset-top)" }}
            >
                <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
                    <button
                        type="button"
                        onClick={() => setMoreOpen((v) => !v)}
                        className="sm:hidden -ml-1 p-1.5 rounded-md hover:bg-zinc-100 text-[#0F1115]"
                        data-testid="portal-top-menu"
                        aria-label="Open menu"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                    <Link to="/portal" className="flex-1 sm:flex-none inline-flex items-center justify-center sm:justify-start gap-2.5" data-testid="portal-brand">
                        <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#0E3B2E] text-white font-display text-[11px] font-bold shrink-0">C</span>
                        <div className="text-center sm:text-left">
                            <div className="text-[15px] font-display font-semibold leading-none tracking-tight" data-testid="portal-tenant-name">ClientOps</div>
                            <div className="mt-1 text-[10px] text-[#5E636E]">Your business. Your data. Your answers.</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="relative p-2 rounded-full hover:bg-zinc-100 text-[#0F1115]"
                            data-testid="portal-bell"
                            aria-label="Notifications"
                        >
                            <Bell className="h-5 w-5" />
                            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" />
                        </button>
                        <div><EntitySwitcher /></div>
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-white px-3 py-1.5 text-xs text-[#5E636E] hover:text-[#0F1115] hover:border-[#0E3B2E] transition-colors"
                            data-testid="portal-logout-btn"
                            aria-label="Sign out"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Sign out
                        </button>
                    </div>
                </div>
                {/* Top tabs (desktop) */}
                <div className="hidden sm:block border-t border-[#E4E4E7] bg-white">
                    <div className="mx-auto max-w-3xl px-4">
                        <nav className="flex items-center gap-1 py-2" data-testid="portal-nav-top">
                            {primaryNav.map((item) => (
                                <NavLink
                                    key={item.key}
                                    to={item.to}
                                    end={item.end}
                                    className={({ isActive }) =>
                                        `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors ${
                                            isActive ? "bg-[#0E3B2E] text-white" : "text-[#5E636E] hover:bg-[#F5F4EF]"
                                        }`
                                    }
                                    data-testid={`portal-nav-${item.key}`}
                                >
                                    <item.icon className="h-4 w-4" />
                                    {item.label}
                                </NavLink>
                            ))}
                            <div ref={moreRef} className="relative ml-auto flex items-center gap-2">
                                {moreNav.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setMoreOpen((v) => !v)}
                                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-[#5E636E] hover:bg-[#F5F4EF]"
                                        data-testid="portal-nav-more"
                                    >
                                        <MoreHorizontal className="h-4 w-4" /> More
                                    </button>
                                )}
                                {features?.upload !== false && (
                                    <NavLink
                                        to="/portal/upload"
                                        className={({ isActive }) =>
                                            `inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium shadow-sm transition-all ${
                                                isActive
                                                    ? "bg-[#0B2F25] text-white"
                                                    : "bg-[#0E3B2E] text-white hover:bg-[#0B2F25] hover:shadow"
                                            }`
                                        }
                                        data-testid="portal-nav-upload"
                                    >
                                        <Upload className="h-4 w-4" />
                                        Upload
                                    </NavLink>
                                )}
                                {moreOpen && moreNav.length > 0 && (
                                    <div className="absolute right-0 top-full mt-1.5 w-48 z-40 rounded-lg border border-[#E4E4E7] bg-white shadow-lg overflow-hidden" data-testid="portal-nav-more-menu">
                                        {moreNav.map((item) => (
                                            <NavLink
                                                key={item.key}
                                                to={item.to}
                                                onClick={() => setMoreOpen(false)}
                                                className={({ isActive }) =>
                                                    `flex items-center gap-2 px-3 py-2 text-sm ${isActive ? "bg-[#F5F4EF] text-[#0E3B2E]" : "text-[#5E636E] hover:bg-zinc-50"}`
                                                }
                                                data-testid={`portal-nav-${item.key}`}
                                            >
                                                <item.icon className="h-4 w-4" /> {item.label}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </nav>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                <div className="mx-auto max-w-3xl px-4 py-6 sm:py-8">
                    <Outlet />
                </div>
            </main>

            {/* PWA install nudge — silent when installed, respects cool-off. */}
            <InstallPrompt />

            {/* Mobile bottom tab bar — matches the "Your business. Your data.
                Your answers." spec: Home · Insights · [Upload big] · Documents
                · Messages. Feature-flag hides items whose flag is off. */}
            <nav
                ref={mobileMoreRef}
                className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#E4E4E7] bg-white/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
                data-testid="portal-bottom-nav"
            >
                <div className="flex items-stretch">
                    {BOTTOM_TABS.filter((t) => {
                        if (t.flag && features?.[t.flag] === false) return false;
                        if (t.addon && features?.[t.addon] !== true) return false;
                        return true;
                    }).map((tab) => {
                        if (tab.raised) {
                            return (
                                <NavLink
                                    key={tab.key}
                                    to={tab.to}
                                    className="flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 text-[10px] leading-tight font-medium"
                                    data-testid={`portal-bottom-${tab.key}`}
                                >
                                    <span className="grid h-11 w-11 place-items-center rounded-full bg-sky-500 shadow-md ring-4 ring-white -mt-4">
                                        <tab.icon className="h-5 w-5 text-white" strokeWidth={2.5} />
                                    </span>
                                    <span className="mt-0.5 text-[10px] text-[#5E636E]">{tab.label}</span>
                                </NavLink>
                            );
                        }
                        return (
                            <NavLink
                                key={tab.key}
                                to={tab.to}
                                end={tab.end}
                                className={({ isActive }) =>
                                    `flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-2 px-1 text-[10px] leading-tight ${
                                        isActive ? "text-[#0E3B2E] font-semibold" : "text-[#5E636E]"
                                    }`
                                }
                                data-testid={`portal-bottom-${tab.key}`}
                            >
                                <tab.icon className="h-5 w-5" />
                                <span className="truncate w-full text-center">{tab.label}</span>
                            </NavLink>
                        );
                    })}
                </div>
                {moreOpen && (
                    <div className="absolute bottom-full inset-x-0 mb-1 mx-2 rounded-lg border border-[#E4E4E7] bg-white shadow-lg overflow-hidden" data-testid="portal-bottom-more-menu">
                        <div className="px-3 py-2 border-b border-[#E4E4E7] bg-[#FAFAFA] flex items-center justify-between">
                            <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">More</div>
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="text-[11px] text-[#5E636E] hover:text-rose-600 inline-flex items-center gap-1"
                            >
                                <LogOut className="h-3 w-3" /> Sign out
                            </button>
                        </div>
                        {[...primaryNav.filter((n) => !["home", "messages"].includes(n.key)), ...moreNav].map((item) => (
                            <NavLink
                                key={item.key}
                                to={item.to}
                                onClick={() => setMoreOpen(false)}
                                className={({ isActive }) =>
                                    `flex items-center gap-2 px-4 py-3 text-sm border-b border-[#E4E4E7] last:border-b-0 ${isActive ? "bg-[#F5F4EF] text-[#0E3B2E]" : "text-[#5E636E]"}`
                                }
                                data-testid={`portal-bottom-more-${item.key}`}
                            >
                                <item.icon className="h-4 w-4" /> {item.label}
                            </NavLink>
                        ))}
                    </div>
                )}
            </nav>
        </div>
        </DataSourceGate>
    );
}

// ---------------------------------------------------------------------------
// FirmOnDesktopScreen — shown when a firm_admin / firm_staff user opens
// /portal (typically on the native mobile app). Explains the split and
// gives them one-tap logout so they can sign in as a client account instead.
// ---------------------------------------------------------------------------
function FirmOnDesktopScreen({ user, onLogout }) {
    return (
        <div
            className="min-h-screen bg-[#F5F4EF] flex flex-col items-center justify-center px-6 text-center"
            style={{
                paddingTop: "max(env(safe-area-inset-top), 1.5rem)",
                paddingBottom: "max(env(safe-area-inset-bottom), 1.5rem)",
            }}
            data-testid="firm-on-portal-guard"
        >
            <div className="max-w-md">
                <div className="mx-auto w-14 h-14 rounded-full bg-[#0E3B2E] grid place-items-center text-white">
                    <Building2 className="h-7 w-7" />
                </div>
                <h1 className="mt-5 font-display text-2xl font-semibold text-[#0F1115]">
                    This is your firm admin login
                </h1>
                <p className="mt-2 text-sm text-[#5E636E] leading-relaxed">
                    The mobile app is built for clients — quick check-ins on their tax reserve, debtors and debt-eradication plan.
                </p>
                <p className="mt-3 text-sm text-[#5E636E] leading-relaxed">
                    To manage clients, run bulk invites, review submissions and sync integrations, please open <strong className="text-[#0F1115]">clientops.nz</strong> on your desktop.
                </p>
                <div className="mt-6 rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-xs text-[#5E636E]">
                    Signed in as <span className="font-mono text-[#0F1115]">{user?.email}</span>
                </div>
                <button
                    type="button"
                    onClick={onLogout}
                    className="mt-4 inline-flex items-center gap-2 rounded-md border border-[#E4E4E7] bg-white px-4 py-2 text-sm hover:bg-zinc-50"
                    data-testid="firm-on-portal-logout"
                >
                    <LogOut className="h-4 w-4" /> Log out & sign in as a client
                </button>
            </div>
        </div>
    );
}
